import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../../App";
import { useLanguage } from "../../../i18n/LanguageContext";

const POLLING_KEY = "mc_active_polling";

function loadPollingState() {
  try {
    const raw = sessionStorage.getItem(POLLING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function savePollingState(state) {
  try {
    sessionStorage.setItem(POLLING_KEY, JSON.stringify(state));
  } catch {}
}

function clearPollingState() {
  sessionStorage.removeItem(POLLING_KEY);
}

export function useQueryPolling() {
  const { t } = useLanguage();
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [partialSynthesis, setPartialSynthesis] = useState("");
  const timerRef = useRef(null);
  const pollingRef = useRef(false);
  const pollTokenRef = useRef(0);

  // Pipeline stages for progress display
  const PIPELINE_STAGES = [
    { key: 'pii', label: t('query.stagePii') || 'PII screening', pct: 5, duration: 2 },
    { key: 'models', label: t('query.stageModels') || 'Models analyzing', pct: 25, duration: 8 },
    { key: 'chairman', label: t('query.stageChairman') || 'Chairman synthesizing', pct: 70, duration: 45 },
    { key: 'saving', label: t('query.stageSaving') || 'Finalizing', pct: 95, duration: 5 },
  ];

  const getCurrentStage = useCallback((elapsed, estimated) => {
    if (!estimated) return PIPELINE_STAGES[0];
    const ratio = elapsed / estimated;
    if (ratio < 0.05) return PIPELINE_STAGES[0];
    if (ratio < 0.20) return PIPELINE_STAGES[1];
    if (ratio < 0.90) return PIPELINE_STAGES[2];
    return PIPELINE_STAGES[3];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getProgressPercent = useCallback((elapsed, estimated) => {
    if (!estimated) return 5;
    const ratio = elapsed / estimated;
    // Ease toward 95%; must stay monotonic when elapsed exceeds the estimate.
    return Math.min(95, 95 * (1 - Math.exp(-3 * ratio)));
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startTimer = (estimated, startedAt) => {
    const now = Date.now();
    const origin = startedAt || now;
    const elapsedSoFar = Math.floor((now - origin) / 1000);
    const est = estimated || 65;

    setElapsedTime(elapsedSoFar);
    setEstimatedTime(est);

    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return origin;
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setEstimatedTime(null);
    setElapsedTime(0);
    clearPollingState();
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
  };

  const pollForResult = async (queryId, { onCompleted, onError }) => {
    const token = ++pollTokenRef.current;
    pollingRef.current = true;
    const stillCurrent = () => pollTokenRef.current === token;

    const finishCurrent = () => {
      if (!stillCurrent()) return;
      stopTimer();
      setPartialSynthesis("");
      pollingRef.current = false;
    };

    const maxAttempts = 120;
    const interval = 2000;

    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, interval));

      try {
        const statusRes = await axios.get(
          `${API}/council/query/${queryId}/status`
        );

        const data = statusRes.data?.data;
        if (!data) continue;

        if (data.status === "completed") {
          finishCurrent();
          onCompleted(data, queryId);
          return;
        } else if (data.status === "error") {
          finishCurrent();
          onError(data.error_message || t('common.error'), queryId);
          return;
        } else if (
          stillCurrent()
          && data.status === "synthesizing"
          && data.partial_synthesis
        ) {
          setPartialSynthesis(data.partial_synthesis);
        }
      } catch (err) {
        console.error("Poll error:", err);
        if (err.response?.status === 401) {
          finishCurrent();
          onError("auth", queryId);
          return;
        }
      }
    }

    if (stillCurrent()) {
      setPartialSynthesis("");
      stopTimer();
      pollingRef.current = false;
      toast.error(t('query.timeout') || "Query timed out");
    }
    onError("timeout", queryId);
  };

  // Persist active polling state for tab-switch recovery
  const startPolling = (queryId, estimatedSeconds, callbacks, {
    hasAttachments = false,
    historyFilesSkipped = false,
  } = {}) => {
    const startedAt = startTimer(estimatedSeconds);
    savePollingState({
      query_id: queryId,
      estimated_seconds: estimatedSeconds,
      started_at: startedAt,
      has_attachments: hasAttachments,
      history_files_skipped: historyFilesSkipped,
    });
    pollForResult(queryId, callbacks);
  };

  // Recover polling on remount if there's an active query
  const recoverPolling = (callbacks) => {
    const saved = loadPollingState();
    if (!saved?.query_id) return null;

    const elapsed = (Date.now() - saved.started_at) / 1000;
    // If more than 5 minutes passed, assume it's stale
    if (elapsed > 300) {
      clearPollingState();
      return null;
    }

    startTimer(saved.estimated_seconds, saved.started_at);
    pollForResult(saved.query_id, callbacks);
    return {
      queryId: saved.query_id,
      hasAttachments: !!saved.has_attachments,
      historyFilesSkipped: !!saved.history_files_skipped,
    };
  };

  // F5 refresh recovery — reuse saved polling state when it matches the session query
  const resumeInterruptedPolling = (queryId, callbacks) => {
    const saved = loadPollingState();
    const hasAttachments = saved?.query_id === queryId ? !!saved.has_attachments : false;
    const historyFilesSkipped = saved?.query_id === queryId
      ? !!saved.history_files_skipped
      : false;

    if (saved?.query_id === queryId) {
      const recovered = recoverPolling(callbacks);
      if (recovered) {
        return recovered;
      }
    }

    startTimer(65);
    pollForResult(queryId, callbacks);
    return { queryId, hasAttachments, historyFilesSkipped };
  };

  return {
    estimatedTime,
    elapsedTime,
    partialSynthesis,
    setPartialSynthesis,
    startTimer,
    stopTimer,
    formatTime,
    pollForResult,
    startPolling,
    recoverPolling,
    getCurrentStage,
    getProgressPercent,
    PIPELINE_STAGES,
  };
}
